import React, { useEffect } from "react";

import ReactPaginate from "react-paginate";
import Select from "react-select";
import {
  useTable,
  useFlexLayout,
  useRowSelect,
  usePagination,
} from "react-table";
import styled from "styled-components";

import { Icon, Spinner } from "..";
import { useLatestRef } from "../../utils";
import { customSelectStyles } from "../FormFields/SelectFormField/styles";
import { SelectAllHeader, SelectRowCell } from "./SelectionColumn";

const PAGE_SIZE_OPTIONS = [
  { label: "20 Items", value: 20 },
  { label: "30 Items", value: 30 },
  { label: "50 Items", value: 50 },
];

export const PaginationWrapper = styled.div`
  ul {
    display: flex;
  }
  li {
    background: #fff;
    color: #111;
    border: 1px solid #d4d4d4;
    min-width: 40px;
    text-align: center;
    white-space: nowrap;
    cursor: pointer;
    user-select: none;
    border-radius: 8px;
    transition: border-color 0.2s cubic-bezier(0.3, 0, 0.5, 1);
    max-width: 46px;
    max-height: 36px;
    a {
      padding: 5px 10px;
      display: block;
      height: 100%;
      width: 100%;
      svg {
        width: 100%;
        height: 100%;
      }
    }
    &:not(:last-child) {
      margin-right: 8px;
    }
    &:not(.disabled, .selected):hover {
      background-color: #fafafa;
    }
    &.disabled a {
      cursor: not-allowed;
      filter: grayscale(1) opacity(0.4);
    }
    &.selected {
      background: #ff643a;
      color: #fff;
      border-color: transparent;
    }
  }
`;

// Module-level so the default keeps a stable identity across renders and never
// re-triggers the selection effect below.
const ALWAYS_SELECTABLE = _row => true;

const Table = ({
  columns,
  data = [],
  isLoading,
  onRowSelect,
  // Rows this table is not allowed to act on. Their checkbox is not rendered
  // AND they are stripped from what `onRowSelect` emits — react-table v7's
  // "select all" toggles every loaded row and offers no per-row opt-out, so
  // hiding the checkbox alone would still leak the row into the selection.
  isRowSelectable = ALWAYS_SELECTABLE,
  // Lets the caller hold back "select all" until it knows which rows are
  // selectable (e.g. the current user's id is still loading).
  canSelectAll = true,
  showPagination,
  pageCount: controlledPageCount,
  fetchData,
  // Defaulted so it stays an optional prop: TypeScript consumers of this JS
  // component infer its props from the destructuring below, and a param without
  // a default is inferred as required -- which fails to compile in consumers
  // that have no filters to reset (e.g. TaskHistory.tsx).
  resetPageKey = "",
  footer,
  showFooter = true,
  showPageSize = true,
  noResultsText = "No results found",
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    selectedFlatRows,
    pageCount,
    gotoPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 20 },
      manualPagination: true,
      pageCount: controlledPageCount,
      // react-table assigns every option onto the instance each render and
      // spreads the instance into Header/Cell renderers, so these reach
      // SelectionColumn already current. Passing them via a ref instead would
      // hand the renderers a stale value: `useLatestRef` writes in a
      // layout effect (after commit) while renderers read during render, and a
      // ref write schedules no re-render — so the change would never show.
      isRowSelectable,
      canSelectAll,
    },
    useFlexLayout,
    usePagination,
    useRowSelect,
    hooks => {
      onRowSelect &&
        hooks.allColumns.push(columns => [
          {
            id: "selection",
            disableResizing: true,
            minWidth: 64,
            width: 64,
            maxWidth: 64,
            Header: SelectAllHeader,
            Cell: SelectRowCell,
          },
          ...columns,
        ]);
      hooks.useInstanceBeforeDimensions.push(({ headerGroups }) => {
        const selectionGroupHeader = headerGroups[0].headers[0];
        selectionGroupHeader.canResize = false;
      });
    },
  );

  const onRowSelectRef = useLatestRef(onRowSelect);
  const onFetchMoreRef = useLatestRef(fetchData);

  const getStyles = (props, align = "left") => [
    props,
    {
      style: {
        justifyContent:
          align === "center"
            ? "center"
            : align === "left"
              ? "flex-start"
              : "flex-end",
        alignItems: "center",
        display: "flex",
      },
    },
  ];

  const handlePageClick = event => {
    gotoPage(event.selected);
  };

  useEffect(() => {
    onFetchMoreRef.current({ pageIndex, pageSize });
  }, [onFetchMoreRef, pageIndex, pageSize]);

  useEffect(() => {
    if (!onRowSelectRef.current) return;

    onRowSelectRef.current(
      selectedFlatRows.map(row => row.original).filter(isRowSelectable),
    );
  }, [selectedFlatRows, onRowSelectRef, isRowSelectable]);

  // Filters/search changed: go back to page 1 so the next fetch uses offset 0.
  // Resetting pageIndex (rather than the consumer's offset) keeps the visible
  // page number and the fetched offset from diverging -- the effect above is the
  // single place that turns a page change into a fetch.
  useEffect(() => {
    gotoPage(0);
  }, [resetPageKey, gotoPage]);

  // The current page no longer exists (rows were removed): clamp to the last
  // page that does, rather than dumping the reader on page 1 from page 10.
  // usePagination allows this -- navigating backwards only requires the target
  // index to be > -1, it is forward navigation that is bounded by pageCount.
  // Deliberately not keyed on pageCount alone: that also fired when the row
  // count merely changed, yanking the user off the page they were reading.
  useEffect(() => {
    if (pageIndex > 0 && pageIndex >= pageCount) {
      gotoPage(Math.max(0, pageCount - 1));
    }
  }, [pageCount, pageIndex, gotoPage]);

  const getHeaderProps = (props, { column }) => getStyles(props, column.align);
  const getCellProps = (props, { cell }) => getStyles(props, cell.column.align);

  return (
    <>
      <div className="overflow-auto block">
        <table
          {...getTableProps()}
          style={{ borderSpacing: "0" }}
          className="w-full"
        >
          <thead className="overflow-x-hidden overflow-y-auto bg-gray-150">
            {headerGroups.map(headerGroup => {
              const { key, ...headerGroupProps } =
                headerGroup.getHeaderGroupProps();
              return (
                <tr key={key} {...headerGroupProps} className="h-12">
                  {headerGroup.headers.map(column => {
                    const { key, ...headerProps } =
                      column.getHeaderProps(getHeaderProps);
                    return (
                      <th
                        key={key}
                        {...headerProps}
                        className="border-b border-custom-gray-300 font-semibold text-gray-700 text-sm uppercase p-4"
                      >
                        {column.render("Header")}
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>

          <tbody
            {...getTableBodyProps()}
            className="overflow-y-auto overflow-x-hidden"
          >
            {!isLoading && !data?.length && (
              <tr>
                <td>
                  <div className="text-center py-20">
                    <span>{noResultsText}</span>
                  </div>
                </td>
              </tr>
            )}
            {isLoading ? (
              <tr>
                <td>
                  <div className="text-center py-20">
                    <Spinner />
                  </div>
                </td>
              </tr>
            ) : (
              rows.map(row => {
                prepareRow(row);
                const { key, ...rowProps } = row.getRowProps();
                return (
                  <tr
                    key={key}
                    {...rowProps}
                    className="min-h-24 border-b border-custom-gray-300"
                  >
                    {row.cells.map(cell => {
                      const { key, ...cellPropsWithoutKey } =
                        cell.getCellProps(getCellProps);
                      return (
                        <td key={key} {...cellPropsWithoutKey} className="p-4">
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {showPagination && (
        <div className="mt-8 flex flex-col items-center lg:justify-between lg:flex-row">
          {showFooter && (
            <div className="table-footer order-1 mt-6 lg:order-none lg:mt-0">
              {footer}
            </div>
          )}
          <div className="flex flex-col items-center flex-wrap-reverse lg:justify-end lg:flex-row">
            {showPageSize && (
              <div className="flex items-center">
                <span className="font-semibold text-black mr-2">Show by</span>
                <div className="w-28 mr-6">
                  <Select
                    value={PAGE_SIZE_OPTIONS.find(
                      option => option.value === pageSize,
                    )}
                    onChange={option => setPageSize(option.value)}
                    options={PAGE_SIZE_OPTIONS}
                    styles={customSelectStyles}
                    menuPlacement="top"
                  />
                </div>
              </div>
            )}
            <PaginationWrapper className="mt-6 lg:mt-0">
              <ReactPaginate
                breakLabel="..."
                nextLabel={<Icon name="chevronRight" />}
                onPageChange={handlePageClick}
                pageRangeDisplayed={2}
                marginPagesDisplayed={1}
                pageCount={pageCount}
                previousLabel={<Icon name="chevronLeft" />}
                renderOnZeroPageCount={null}
                forcePage={pageIndex}
              />
            </PaginationWrapper>
          </div>
        </div>
      )}
    </>
  );
};

export default Table;
